import { OrganizationRouteHandler } from '@/components/OrganizationRouteHandler'
import { ClusterProvider } from '@/contexts/useCluster'
import { ClusterUpdatesProvider } from '@/contexts/useClusterUpdates'
import { OrganizationProvider } from '@/contexts/useOrganization'
import { ProductCatalogProvider } from '@/contexts/useProductCatalog'
import { RBACPermissionsProvider } from '@/contexts/useRBACPermissions'
import { TemplatesProvider } from '@/contexts/useTemplates'
import { debugModeAvailable } from '@/scripts/environments'
import type { IOktaContext } from '@okta/okta-react/bundles/types/OktaContext'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import {
  Outlet,
  createRootRouteWithContext,
  createRoute,
  redirect,
} from '@tanstack/react-router'
import { TanStackRouterDevtools } from '@tanstack/router-devtools'
import { Suspense, lazy } from 'react'
import { App } from '../App'

type RootContext = {
  auth: IOktaContext
}

export const rootRoute = createRootRouteWithContext<RootContext>()({
  component: () => {
    // Create TanStack Query Client
    const queryClient = new QueryClient()

    // As ReactQueryDevtools can only be seen when NODE_ENV === 'development',
    // lazy load ReactQueryDevtoolsProduction to be in control of when to show it.
    // We want this tool to be visible on staging for debugging purposes.
    const ReactQueryDevtoolsProduction = lazy(() =>
      import('@tanstack/react-query-devtools/production').then((d) => ({
        default: d.ReactQueryDevtools,
      })),
    )

    return (
      <QueryClientProvider client={queryClient}>
        <Outlet />

        {debugModeAvailable && (
          <>
            <TanStackRouterDevtools />
            <Suspense fallback={null}>
              <ReactQueryDevtoolsProduction />
            </Suspense>
          </>
        )}
      </QueryClientProvider>
    )
  },
})

export const applicationRootRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '__VIRTUAL_ROOT__APPLICATION__',
  component: () => (
    <RBACPermissionsProvider>
      <App>
        <OrganizationProvider>
          <OrganizationRouteHandler />
          <Outlet />
        </OrganizationProvider>
      </App>
    </RBACPermissionsProvider>
  ),
  beforeLoad: async ({ context: { auth }, location }) => {
    // In a real world scenario this would probably be a reusable function 'ensureAuthenticated'
    if (!(await auth.oktaAuth.isAuthenticated())) {
      throw redirect({
        to: '/login',
        search: {
          redirect: location.href,
        },
      })
    }
  },
})

/**
 * Acts as a virtual root for cluster routes
 * This ensures a structure where all cluster routes are nested under this root
 * But this can be updated later on and include a path which nest the <Outlet />
 */
export const clustersRootRoute = createRoute({
  getParentRoute: () => applicationRootRoute,
  path: 'clusters',
  component: () => (
    <ClusterProvider>
      <ClusterUpdatesProvider>
        <TemplatesProvider>
          <ProductCatalogProvider>
            <Outlet />
          </ProductCatalogProvider>
        </TemplatesProvider>
      </ClusterUpdatesProvider>
    </ClusterProvider>
  ),
})

/**
 * Acts as a virtual root for cluster updates routes
 * This ensures proper sidebar navigation highlighting
 */
export const clusterUpdatesRootRoute = createRoute({
  getParentRoute: () => applicationRootRoute,
  path: 'clusters/updates',
  component: () => (
    <ClusterProvider>
      <ClusterUpdatesProvider>
        <TemplatesProvider>
          <Outlet />
        </TemplatesProvider>
      </ClusterUpdatesProvider>
    </ClusterProvider>
  ),
})

/**
 * Acts as a virtual root for template routes
 * This ensures a structure where all template routes are nested under this root
 * But this can be updated later on and include a path which nest the <Outlet />
 */
export const templatesRootRoute = createRoute({
  getParentRoute: () => applicationRootRoute,
  path: 'templates',
  component: () => <Outlet />,
})

/**
 * Acts as a virtual root for hives overview routes
 * This ensures a structure where all hives overview routes are nested under this root
 * Includes ClusterProvider to enable cluster operations like deletion
 */
export const hivesOverviewRootRoute = createRoute({
  getParentRoute: () => applicationRootRoute,
  id: '__VIRTUAL_APP_ROOT__', // by not specifying a path, this maps to '/' - see hivesOverviewRoute. ClusterProvider is needed here to enable cluster deletion
  component: () => (
    <ClusterProvider>
      <Outlet />
    </ClusterProvider>
  ),
})

export const releaseNotesRootRoute = createRoute({
  getParentRoute: () => applicationRootRoute,
  path: 'release-notes',
  component: () => <Outlet />,
})

/**
 * Acts as a virtual root for audit logs routes
 * This ensures proper sidebar navigation highlighting
 */
export const auditLogsRootRoute = createRoute({
  getParentRoute: () => applicationRootRoute,
  path: 'audit-logs',
  component: () => <Outlet />,
})
